import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Alert from "@cloudscape-design/components/alert";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { providerAddModalStore } from "../stores/providerAddModal";
import { providerOptionsStore } from "../stores/provider";
import { prepareAddProviderPayload } from "./providerValidation";

const toCloudscapeOptions = (codes) =>
	Array.isArray(codes) ? codes.map((code) => ({ label: code, value: code })) : [];

class ProviderAddModal extends Component {
	constructor(props) {
		super(props);
	}


	addProviders = async () => {
		const { providerAddModal, providerOptions, setProviderAddModal, Interface } = this.props;
		const validation = prepareAddProviderPayload(
			{
				id: providerAddModal.id,
				name: providerAddModal.name,
				path: providerAddModal.path,
				order: providerAddModal.order,
				listed: providerAddModal.listed?.value,
				active: providerAddModal.active?.value,
				integrationType: providerAddModal.integrationType,
				restrictedCountries: providerAddModal.restrictedCountries,
				currency: providerAddModal.currency,
				fee: providerAddModal.fee,
			},
			providerOptions,
		);

		if (!validation.payload) {
			setProviderAddModal({
				...providerAddModal,
				loaded: true,
				fieldErrors: validation.errors,
				error: {
					text: "Fix the highlighted Provider fields before saving.",
					code: 0,
					timestamp: Date.now(),
				},
			});
			return;
		}

		setProviderAddModal({ ...providerAddModal, loaded: false, fieldErrors: {} });

		try {
			await Interface.request(
				"set",
				{
					class: "data",
					module: "providers",
					method: "add",
				},
				validation.payload,
			);

			Interface.addNoty("Success", "Providers were successfully added");
			Interface.loadProvider();
			setProviderAddModal({
				...providerAddModal,
				show: false,
				loaded: true,
				name: null,
				path: null,
				order: null,
				listed: null,
				active: null,
				id: null,
				integrationType: null,
				restrictedCountries: [],
				currency: [],
				fee: null,
				fieldErrors: {},
				error: { text: "", code: 0, timestamp: 0 },
			});
		} catch (error) {
			Interface.addNoty("Error", error.text || error.toString());
			setProviderAddModal({
				...providerAddModal,
				loaded: true,
				error: {
					text: error.text || error.toString(),
					code: error.code || 500,
					timestamp: Date.now(),
				},
			});
		}
	};

	updateParam = (key, value) => {
		const { providerAddModal, setProviderAddModal } = this.props;
		const updatedState = cloneDeep(providerAddModal);
		updatedState[key] = value;
		updatedState.fieldErrors = updatedState.fieldErrors || {};
		delete updatedState.fieldErrors[key];
		updatedState.error = { text: "", code: 0, timestamp: 0 };
		setProviderAddModal(updatedState);
	};

	render() {
		const { providerAddModal, providerOptions, setProviderAddModal } = this.props;
		const fieldErrors = providerAddModal.fieldErrors || {};
		const integrationTypeOptions = toCloudscapeOptions(providerOptions?.integrationTypes);
		const countryOptions = toCloudscapeOptions(providerOptions?.countryCodes);
		const currencyOptions = toCloudscapeOptions(providerOptions?.currencyCodes);

		return (
			<Modal
				visible={providerAddModal.show}
				onDismiss={() =>
					setProviderAddModal({
						...providerAddModal,
						show: false,
						fieldErrors: {},
						error: { text: "", code: 0, timestamp: 0 },
					})
				}
				header="Add Providers"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								variant="link"
								onClick={() =>
									setProviderAddModal({
										...providerAddModal,
										show: false,
										fieldErrors: {},
										error: { text: "", code: 0, timestamp: 0 },
									})
								}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								onClick={this.addProviders}
								loading={!providerAddModal.loaded}
								disabled={!providerAddModal.loaded}
							>
								Add provider
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween size="m">
					{providerAddModal.error.text && (
						<Alert type="error" children={providerAddModal.error.text} />
					)}
					<FormField label="id" description="Enter a numeric value for id (no required)" errorText={fieldErrors.id}>
						<Input
							type="number"
							value={providerAddModal.id ?? ""}
							onChange={({ detail }) => this.updateParam("id", detail.value || null)}
							disabled={!providerAddModal.loaded}
						/>
					</FormField>
					<FormField label="Name" errorText={fieldErrors.name}>
						<Input
							value={providerAddModal.name || ""}
							onChange={({ detail }) => this.updateParam("name", detail.value || null)}
							disabled={!providerAddModal.loaded}
						/>
					</FormField>
					<FormField label="Path" errorText={fieldErrors.path}>
						<Input
							value={providerAddModal.path || ""}
							onChange={({ detail }) => this.updateParam("path", detail.value || null)}
							disabled={!providerAddModal.loaded}
						/>
					</FormField>
					<FormField label="Order" description="Enter a numeric value for order" errorText={fieldErrors.order}>
						<Input
							type="number"
							value={providerAddModal.order ?? ""}
							onChange={({ detail }) => this.updateParam("order", detail.value || null)}
							disabled={!providerAddModal.loaded}
						/>
					</FormField>
					<FormField label="Listed" errorText={fieldErrors.listed}>
						<Select
							selectedOption={providerAddModal.listed}
							onChange={({ detail }) => this.updateParam("listed", detail.selectedOption)}
							options={[
								{ value: true, label: "True" },
								{ value: false, label: "False" },
							]}
							placeholder="Choose listed status"
							disabled={!providerAddModal.loaded}
						/>
					</FormField>
					<FormField label="Active" errorText={fieldErrors.active}>
						<Select
							selectedOption={providerAddModal.active}
							onChange={({ detail }) => this.updateParam("active", detail.selectedOption)}
							options={[
								{ value: true, label: "True" },
								{ value: false, label: "False" },
							]}
							placeholder="Choose active status"
							disabled={!providerAddModal.loaded}
						/>
					</FormField>
					<FormField label="Integration Type" errorText={fieldErrors.integrationType}>
						<Select
							selectedOption={integrationTypeOptions.find((option) => option.value === providerAddModal.integrationType) || null}
							onChange={({ detail }) => this.updateParam("integrationType", detail.selectedOption.value)}
							options={integrationTypeOptions}
							placeholder="Choose Integration Type"
							disabled={!providerAddModal.loaded}
						/>
					</FormField>
					<FormField label="Restricted Countries" errorText={fieldErrors.restrictedCountries}>
						<Multiselect
							filteringType="auto"
							selectedOptions={countryOptions.filter((option) => (providerAddModal.restrictedCountries || []).includes(option.value))}
							onChange={({ detail }) => this.updateParam("restrictedCountries", detail.selectedOptions.map((option) => option.value))}
							options={countryOptions}
							placeholder="Choose restricted countries"
							disabled={!providerAddModal.loaded}
						/>
					</FormField>
					<FormField label="Currency" errorText={fieldErrors.currency}>
						<Multiselect
							filteringType="auto"
							selectedOptions={currencyOptions.filter((option) => (providerAddModal.currency || []).includes(option.value))}
							onChange={({ detail }) => this.updateParam("currency", detail.selectedOptions.map((option) => option.value))}
							options={currencyOptions}
							placeholder="Choose currencies"
							disabled={!providerAddModal.loaded}
						/>
					</FormField>
					<FormField label="Fee" description="Enter a numeric value for fee" errorText={fieldErrors.fee}>
						<Input
							type="number"
							step="0.01"
							value={providerAddModal.fee ?? ""}
							onChange={({ detail }) => this.updateParam("fee", detail.value || null)}
							disabled={!providerAddModal.loaded}
						/>
					</FormField>
				</SpaceBetween>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const providerAddModal = useRecoilValue(providerAddModalStore);
		const setProviderAddModal = useSetRecoilState(providerAddModalStore);
		const providerOptions = useRecoilValue(providerOptionsStore);

		return (
			<Component
				{...props}
				providerOptions={providerOptions}
				setProviderAddModal={setProviderAddModal}
				providerAddModal={providerAddModal}
			/>
		);
	};
}

export default withRecoil(ProviderAddModal);
